import { type FunctionComponent, memo, useCallback } from 'react';
import { useNavigate } from 'react-router';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import type { JobArchitecture } from '~/shared/data-contract/job-architecture';

import { useUserSettingsUiMutation } from '../../app/query-hooks/use-user-settings-ui';
import { JobArchitectureSelect } from '../../job-architecture-v2';

type HeaderProps = {
    readonly activeJobArchitectureId: string;
};

export const Header: FunctionComponent<HeaderProps> = memo(({ activeJobArchitectureId }) => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const { validatingCobrainerApiFetcher } = useApplicationContext();
    const {
        userSettingsUiMutation: { mutate },
    } = useUserSettingsUiMutation(validatingCobrainerApiFetcher);

    const handleSwitchArchitecture = useCallback(
        (architecture: JobArchitecture): void => {
            mutate({ selectedJobArchitectureId: architecture.id });
            navigate({ pathname: `/installation-guide/${architecture.id}`, search: '' }, { replace: true });
        },
        [mutate, navigate],
    );

    return (
        <div className="flex flex-col gap-3xl">
            <div className="flex flex-col gap-m">
                <div className="flex flex-row items-start justify-between gap-xl">
                    <h1 className="m-0 flex-1 text-[2.25rem] font-semibold leading-[2.75rem] tracking-[-0.025em] text-black-900">
                        {translate('pages.installation-guide.welcome.heading')}
                    </h1>
                    <div className="shrink-0">
                        <JobArchitectureSelect
                            activeJobArchitectureId={activeJobArchitectureId}
                            onSwitchArchitecture={handleSwitchArchitecture}
                        />
                    </div>
                </div>
                <p className="m-0 text-base font-normal leading-[1.5rem] text-gray-800">
                    {translate('pages.installation-guide.welcome.subline')}
                </p>
            </div>
        </div>
    );
});
